import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { TypedChartOfAccountsTemplateUpload } from "@common/helpers/chartOfAccountsTemplateTypeMapping";
import { ChartOfAccountsTemplateTypeMappingDrawer } from "./ChartOfAccountsTemplateTypeMappingDrawer";
import { ChartOfAccountsTemplateUploadModal } from "./ChartOfAccountsTemplateUploadModal";
import { ChartOfAccountsTemplateUploadSourcePicker } from "./ChartOfAccountsTemplateUploadSourcePicker";
import { QUICKBOOKS_CONNECTION_SOURCE } from "./uploadSources";
import { useImportChartOfAccountsTemplateFromQuickbooks } from "./useImportChartOfAccountsTemplateFromQuickbooks";
import { ChartOfAccountsTemplateUploadFlowState } from "./useChartOfAccountsTemplateUploadFlow";

interface Props {
    title: string;
    flow: ChartOfAccountsTemplateUploadFlowState;
    onClose: () => void;
    onTypesConfirmed: (chart: TypedChartOfAccountsTemplateUpload) => void;
}

export const ChartOfAccountsTemplateUploadSteps = ({
    title,
    flow,
    onClose,
    onTypesConfirmed,
}: Props) => {
    const { step } = flow;
    const requestClose = () => flow.requestClose(onClose);
    const quickbooksImport = useImportChartOfAccountsTemplateFromQuickbooks({
        onImported: flow.continueToTypeMapping,
    });

    return (
        <>
            <ChartOfAccountsTemplateUploadSourcePicker
                title={title}
                show={step.name === "source"}
                onClose={onClose}
                isPending={quickbooksImport.isPending}
                onNext={(source) =>
                    source === QUICKBOOKS_CONNECTION_SOURCE
                        ? quickbooksImport.start()
                        : flow.chooseSource(source)
                }
            />
            {step.name === "upload" && (
                <ChartOfAccountsTemplateUploadModal
                    title={title}
                    source={step.source}
                    draft={step.draft}
                    onClose={requestClose}
                    onDraftChange={flow.replaceDraft}
                    onNext={({ chart }) => flow.continueToTypeMapping(chart)}
                />
            )}
            {step.name === "typeMapping" && (
                <ChartOfAccountsTemplateTypeMappingDrawer
                    importedChart={step.chart}
                    isDiscardPromptOpen={flow.isDiscardPromptOpen}
                    onClose={requestClose}
                    onContinue={onTypesConfirmed}
                />
            )}
            <ConfirmationModal
                show={flow.isDiscardPromptOpen}
                title="Discard imported accounts?"
                question="The imported accounts aren't saved. Closing now discards them."
                yes="Discard"
                yesBtnVariant="destructive"
                no="Keep editing"
                onReject={flow.closeDiscardPrompt}
                onHide={flow.closeDiscardPrompt}
                onConfirm={onClose}
            />
        </>
    );
};
